import { AfterViewChecked, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { SafeHtml } from '@angular/platform-browser';
import { Subject } from 'rxjs';
import { takeUntil, throttleTime } from 'rxjs/operators';
import { DrawingInspectionDetails } from '../../../../viewmodels/idms/drawing-inspection-details';
import { DrawingPreviewClickPosition } from './drawing-preview-click-position';
import { DrawingPreviewInspectionPointService } from './drawing-preview-inspection-point.service';
import { DrawingPreviewService } from './drawing-preview.service';

@Component({
  selector: 'hb-drawing-preview',
  templateUrl: './drawing-preview.component.html',
  styleUrls: ['./drawing-preview.component.scss'],
})
export class DrawingPreviewComponent implements OnInit, OnDestroy, AfterViewChecked {
  svg: SafeHtml;
  svgElement: SVGSVGElement;

  private _mediaItemDetailsId: string;

  get mediaItemDetailsId(): string {
    return this._mediaItemDetailsId;
  }

  @Input() set mediaItemDetailsId(value: string) {
    this._mediaItemDetailsId = value;
    this.svg = null;
    this.svgElement = null;
    this.drawingPreviewService.loadDrawing(this._mediaItemDetailsId);
  }

  @Input() showNavigation: boolean = true;

  @Output() canvasClick: EventEmitter<DrawingPreviewClickPosition> = new EventEmitter<DrawingPreviewClickPosition>();
  @Output() previewLoaded = new EventEmitter<boolean>();

  @ViewChild('svgContainer', { static: false }) svgContainer: ElementRef;

  private unsubscribe: Subject<boolean> = new Subject();
  drawingInspectionDetails: DrawingInspectionDetails;
  private redraw = new Subject();
  private isLoadingSvg: boolean;

  constructor(
    public drawingPreviewService: DrawingPreviewService,
    private inspectionPointService: DrawingPreviewInspectionPointService,
    private changeDetectorRef: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    this.drawingPreviewService.inspectionPointAdded.pipe(takeUntil(this.unsubscribe)).subscribe(inspectionPoint => {
      if (inspectionPoint && inspectionPoint.svgPosition.x > 0 && inspectionPoint.svgPosition.y > 0) {
        inspectionPoint.svgPosition.isDisplayed = true;
        this.inspectionPointService.addInspectionPoint(inspectionPoint);
        this.drawingPreviewService.loadEquipmentData(this.drawingInspectionDetails);
      }
    });

    this.drawingPreviewService.svg.pipe(takeUntil(this.unsubscribe)).subscribe(svg => {
      this.svg = svg;
      this.svgElement = null;
      if (this.svgContainer && this.svgContainer.nativeElement) {
        this.svgContainer.nativeElement = null;
      }

      this.isLoadingSvg = true;
    });

    this.drawingPreviewService.inspectionPointDeleted.pipe(takeUntil(this.unsubscribe)).subscribe(inspectionPoint => {
      if (inspectionPoint) {
        this.inspectionPointService.deleteInspectionPoint(inspectionPoint);
      }
    });

    this.drawingPreviewService.drawingInspectionDetails.pipe(takeUntil(this.unsubscribe)).subscribe(details => {
      if (details) {
        this.drawingInspectionDetails = details;

        if (this.svgElement) {
          this.inspectionPointService.reloadInspectionPoints(details.inspectionPoints);
        }

        this.previewLoaded.emit(true);
      }
    });

    this.redraw.pipe(takeUntil(this.unsubscribe), throttleTime(250)).subscribe(() => {
      if (this.svgElement && this.drawingInspectionDetails) {
        this.inspectionPointService.reloadInspectionPoints(this.drawingInspectionDetails.inspectionPoints);
      }
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe.next(true);
    this.unsubscribe.complete();
  }

  ngAfterViewChecked() {
    if (
      this.svg &&
      this.isLoadingSvg &&
      this.svgContainer.nativeElement &&
      this.svgContainer.nativeElement.childElementCount > 0 &&
      this.drawingInspectionDetails
    ) {
      const svgElement: SVGSVGElement = this.svgContainer.nativeElement.getElementsByTagName('svg')[0];

      if (!svgElement.width.baseVal.value) {
        return;
      }

      this.isLoadingSvg = false;
      this.svgElement = svgElement;

      this.svgElement.style.pointerEvents = 'all';

      this.inspectionPointService.initializePreviewInspectionPoints(this.svgElement, this.drawingInspectionDetails.inspectionPoints);

      this.changeDetectorRef.detectChanges();

      this.svgElement.addEventListener('click', (e: any) => {
        const svgPoint = this.svgElement.createSVGPoint();
        svgPoint.x = e.clientX;
        svgPoint.y = e.clientY;

        const transformedPoint = svgPoint.matrixTransform(this.svgElement.getScreenCTM().inverse());

        const location = new DrawingPreviewClickPosition(transformedPoint.x, transformedPoint.y);

        this.canvasClick.emit(location);
      });
    }
  }

  onZoom(event: any) {
    this.redraw.next();
  }

  onResize(event: any) {
    this.redraw.next();
  }
}
